<template>
    <div class="users-autocomplete">
        <form-wrapper :validator="$v" class="form-wrapper">
            <form-group name="selectedAutocomplete" :label="label" attribute="User Name">
                <md-input
                    ref="usersAutocomplete"
                    type="text"
                    v-model="autocompleteText"
                    @focus="onFocus($event)"
                    @focusout="onFocusout($event)"
                    @blur="blur"
                    @change="onChange"
                    @keyup.down="onArrowDown"
                    @keyup.up="onArrowUp"
                    @keyup.enter="onEnter"
                    :required="isRequired"
                    v-focus="shouldFocus"
                />
            </form-group>
        </form-wrapper>
        <div class="autocomplete">
            <ul
                id="autocomplete-results"
                v-show="isOpen && autocompleteList.length"
                class="autocomplete-results"
                ref="autocompleteContainer"
            >
                <li class="loading" v-if="isAutoCompleteLoading">
                    Loading results...
                </li>
                <li
                    ref="autocompleteOptions"
                    v-else
                    v-for="(result, i) in autocompleteList"
                    :key="result.publicUserId"
                    @click="setAutocompleteResult(result)"
                    class="autocomplete-result"
                    :class="{ 'is-active': i === arrowCounter }"
                >
                    {{ result.fullName }} ( {{ result.email }} )
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { handleRequests } from '@/helpers';

export default {
    name: 'SupportUsersAutocomplete',
    props: {
        classname: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: 'Name'
        },
        icon: {
            type: String,
            default: ''
        },
        shouldFocus: {
            type: Boolean,
            default: false
        },
        value: {
            type: Object,
            default() {
                return { publicUserId: null };
            }
        },
        isRequired: {
            type: Boolean,
            default: true
        }
    },
    validations: {
        selectedAutocomplete: {
            required: (value, vm) => {
                if (!vm.isRequired) 
                    return true;
                if (vm.selectedAutocomplete === null) 
                    return false;
                return true;
            }
        }
    },
    data() {
        return {
            autocompleteText: null,
            autocompleteList: [],
            isOpen: false,
            isAutoCompleteLoading: false,
            arrowCounter: 0,
            selectedAutocomplete: null
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    watch: {
        // eslint-disable-next-line func-names
        autocompleteText: _.debounce(function() {
            if (
                this.selectedAutocomplete !== null &&
                this.autocompleteText !== `${this.selectedAutocomplete.fullName} ( ${this.selectedAutocomplete.email} )`
            )
                this.selectedAutocomplete = null;
            if (this.selectedAutocomplete === null) 
                this.getUsersList();
        }, 500)
    },
    async mounted() {
        this.update('');
    },
    methods: {
        callTouch() {
            this.$v.$touch();
        },
        onFocus(e) {
            // for selecting the whole input field
            setTimeout(() => e.target.select(), 0);
            this.$emit('focus');
        },
        onFocusout(e) {
            setTimeout(() => {
                if (this.selectedAutocomplete === null) {
                    this.setAutocompleteResult(null);
                } else {
                    this.isOpen = false;
                }
                this.arrowCounter = -1;
            }, 300);
        },
        onChange() {
            this.$emit('change', this.autocompleteText);
        },
        focus() {
            this.$refs.usersAutocomplete.$el.focus();
        },
        blur() {
            this.$refs.usersAutocomplete.$el.blur();
            // a timeout is needed since when an autocomplete option is selected it is synchronous with the setAutoCompleteResult function
            // we need to make sure that setAutocompleteResult runs first before we close the autocomplete selection.
            // This is also used when users use the tab key to go to the next input. We need to close the autocomplete selection.
            setTimeout(() => {
                this.isOpen = false;
            }, 300);
        },
        update(value) {
            this.autocompleteText = value;
        },
        setAutocompleteResult(data) {
            if (data != null) {
                this.selectedAutocomplete = data;
                this.autocompleteText = `${data.fullName} ( ${data.email} )`;
            } else {
                this.selectedAutocomplete = null;
                this.autocompleteText = '';
            }
            const val = Object.assign({}, data);

            // this.$emit('input', this.selectedAutocomplete.publicUserId);
            this.$emit('input', val);
            this.isOpen = false;
        },
        onArrowDown(evt) {
            if (this.arrowCounter < this.autocompleteList.length - 1) {
                this.arrowCounter = this.arrowCounter + 1;
                this.fixScrolling();
            }
        },
        onArrowUp() {
            if (this.arrowCounter > 0) {
                this.arrowCounter = this.arrowCounter - 1;
                this.fixScrolling();
            }
        },
        onEnter() {
            this.setAutocompleteResult(this.autocompleteList[this.arrowCounter]);
            this.arrowCounter = -1;
        },
        fixScrolling() {
            const optionHeight = this.$refs.autocompleteOptions[this.arrowCounter].clientHeight;
            this.$refs.autocompleteContainer.scrollTop = optionHeight * this.arrowCounter;
        },
        // eslint-disable-next-line func-names
        async getUsersList() {
            this.isAutoCompleteLoading = true;
            const endpoint = `/api/support/users`;

            const {
                data: { users }
            } = await handleRequests(endpoint, {
                params: {
                    pageNumber: 1,
                    itemsPerPage: 50,
                    searchText: this.autocompleteText
                }
            });

            this.isOpen = true;
            this.autocompleteList = users;
            this.isAutoCompleteLoading = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.users-autocomplete {
    position: relative;
    width: 100%;
}

.autocomplete {
    position: absolute;
    width: 100%;
    background-color: #fff;
    z-index: 9;
    top: 40px;
}

.autocomplete-results {
    padding: 0;
    margin: 0;
    border: 1px solid #eeeeee;
    height: 120px;
    overflow: auto;
    width: 100%;
}

.autocomplete-result {
    list-style: none;
    text-align: left;
    padding: 4px 2px;
    cursor: pointer;
}

.autocomplete-result.is-active,
.autocomplete-result:hover {
    background-color: #4aae9b;
    color: white;
}
</style>
